import { jsPDF } from "jspdf";

// Helper to format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

// Helper to format date ISO strings to DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("pt-BR");
  } catch (e) {
    return dateStr;
  }
};

// Draw section header in PDF
const drawSectionHeader = (doc, title, y) => {
  doc.setFillColor(241, 245, 249);
  doc.rect(15, y, 180, 7, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59); // Slate 800
  doc.text(title.toUpperCase(), 18, y + 5);
  doc.setDrawColor(203, 213, 225); // Slate 300
  doc.line(15, y, 195, y);
  doc.line(15, y + 7, 195, y + 7);
  return y + 12;
};

// Draw label-value pair
const drawField = (doc, label, value, x, y, size = 9) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139); // Slate 500
  doc.text(label.toUpperCase(), x, y);
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(size);
  doc.setTextColor(15, 23, 42); // Slate 900
  doc.text(String(value || "-"), x, y + 4.5);
};

// Output PDF helper (either saves or returns blob/filename)
const outputPDF = (doc, filename, returnBlob) => {
  if (returnBlob) {
    return {
      blob: doc.output("blob"),
      filename
    };
  }
  doc.save(filename);
  return true;
};

// Helper to dynamically load an image from URL into an Image element
const loadImage = (url) => {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // Resolve with null on failure to prevent app from breaking
    img.src = url;
  });
};

// Helper to draw company details in the footer
const drawCompanyFooter = (doc, config) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139); // Slate 500

  const compName = config?.companyName || config?.contratante || "Sempre Joias";
  const addressParts = [
    config?.companyAddress || "",
    config?.companyBairro ? `Bairro: ${config.companyBairro}` : "",
    config?.companyCidade ? `Cidade: ${config.companyCidade}` : "",
    config?.companyCep ? `CEP: ${config.companyCep}` : ""
  ].filter(Boolean).join(", ");

  const contactParts = [
    config?.companyPhone ? `Tel: ${config.companyPhone}` : "",
    config?.companyEmail ? `E-mail: ${config.companyEmail}` : ""
  ].filter(Boolean).join(" | ");

  const line1 = compName;
  const line2 = addressParts;
  const line3 = contactParts;

  // Render centered in the footer area (above the frame's bottom border at 287)
  if (line1) doc.text(line1, 105, 277, { align: "center" });
  if (line2) doc.text(line2, 105, 281, { align: "center" });
  if (line3) doc.text(line3, 105, 284.5, { align: "center" });
};

// Helper to draw company signature above lines
const drawEmployerSignature = (doc, posY, signatureImg) => {
  if (signatureImg) {
    try {
      // Overlay the signature image centered above the left signature line (x=18 to x=90)
      doc.addImage(signatureImg, "PNG", 34, posY - 12, 26, 11);
    } catch (e) {
      console.error("Error drawing signature image in PDF:", e);
    }
  }
};

// Draw standardized company header block
const drawCompanyTitleHeader = (doc, config, titleText, logoImg = null) => {
  doc.setDrawColor(30, 41, 59);
  doc.setLineWidth(0.5);
  doc.rect(10, 10, 190, 277);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(30, 41, 59);
  doc.text(titleText.toUpperCase(), 105, 20, { align: "center" });
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139); // Slate 500
  
  const compInfo = [
    config?.companyName || config?.contratante || "Sempre Joias",
    config?.companyCnpj ? `CNPJ: ${config.companyCnpj}` : "",
    config?.companyPhone ? `Tel: ${config.companyPhone}` : "",
    config?.companyEmail ? `E-mail: ${config.companyEmail}` : ""
  ].filter(Boolean).join(" | ");
  
  doc.text(compInfo, 105, 24.5, { align: "center" });
  if (config?.companyAddress) {
    doc.text(`Endereço: ${config.companyAddress}`, 105, 28, { align: "center" });
  }

  if (logoImg) {
    try {
      doc.addImage(logoImg, "PNG", 15, 13, 14, 14);
    } catch (e) {
      console.error("Error drawing logo image in PDF:", e);
    }
  }

  doc.setDrawColor(226, 232, 240);
  doc.line(15, 31, 195, 31);

  // Standard footer for framed layouts
  drawCompanyFooter(doc, config);
};

const getCompanyCityState = (config) => {
  if (config?.companyCidade) {
    const cleaned = config.companyCidade.trim();
    if (cleaned.includes("-")) {
      return cleaned.split("-").map(s => s.trim()).join("/");
    }
    return cleaned;
  }
  return "São Paulo/SP";
};

export const colaboradorDocumentGenerator = {
  // 1. FICHA DE REGISTRO DE EMPREGADO
  async generateFichaRegistro(colab, config, returnBlob = false, preloadedLogo = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);
    const signatureImg = config?.companyAssinaturaUrl ? await loadImage(config.companyAssinaturaUrl) : null;

    // Top Header Banner
    doc.setFillColor(30, 41, 59); // Slate 800
    doc.rect(0, 0, 210, 32, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("FICHA DE REGISTRO DE EMPREGADO", 15, 14);
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const companyHeader = `${config?.companyName || config?.contratante || "Sempre Joias"} - Controle Administrativo e de Pessoal`;
    doc.text(companyHeader, 15, 20);

    const admissionDate = config?.data_admissao ? formatDate(config.data_admissao) : "-";
    doc.text(`Data de Emissão: ${new Date().toLocaleDateString("pt-BR")}`, 145, 14);
    doc.text(`Status: ${config?.ativo !== false ? "ATIVO" : "DESLIGADO"}`, 145, 20);

    if (logoImg) {
      try {
        doc.addImage(logoImg, "PNG", 180, 6, 16, 16);
      } catch (e) {
        console.error("Error drawing logo in Ficha de Registro:", e);
      }
    }

    let y = 42;

    // 1. DADOS PESSOAIS
    y = drawSectionHeader(doc, "1. Dados Pessoais do Colaborador", y);
    
    drawField(doc, "Nome Completo", config?.nome_completo || colab?.name, 18, y, 9.5);
    drawField(doc, "CPF", config?.cpf, 110, y);
    drawField(doc, "Data de Nascimento", config?.data_nascimento ? formatDate(config.data_nascimento) : "-", 150, y);

    y += 14;
    drawField(doc, "Endereço Completo", config?.endereco, 18, y);
    drawField(doc, "Telefone", colab?.phone, 110, y);
    drawField(doc, "E-mail de Contato", colab?.email, 150, y);

    y += 14;
    drawField(doc, "Tamanho de Camiseta", config?.tamanho_camiseta, 18, y);
    drawField(doc, "Tamanho de Calçado (EPI)", config?.tamanho_calcado, 60, y);
    drawField(doc, "Matrícula eSocial", config?.matricula_esocial, 110, y);

    y += 20;

    // 2. DADOS BANCÁRIOS
    y = drawSectionHeader(doc, "2. Dados Bancários & Pagamentos", y);

    drawField(doc, "Instituição Bancária", config?.banco, 18, y);
    drawField(doc, "Agência", config?.agencia, 70, y);
    drawField(doc, "Número da Conta", config?.conta, 110, y);
    drawField(doc, "Chave PIX", config?.pix_chave, 150, y);

    y += 20;

    // 3. INFORMAÇÕES DO CONTRATO
    y = drawSectionHeader(doc, "3. Informações Contratuais", y);

    let empStr = config?.companyName || config?.contratante || "Sempre Joias";
    if (config?.companyCnpj) empStr += ` (CNPJ: ${config.companyCnpj})`;
    drawField(doc, "Empresa Contratante", empStr, 18, y);
    drawField(doc, "Data de Admissão", admissionDate, 110, y);
    drawField(doc, "Salário Base", config?.salario ? formatCurrency(config.salario) : "R$ 0,00", 150, y);

    y += 14;
    let compContactInfo = "-";
    if (config?.companyAddress || config?.companyPhone || config?.companyEmail) {
      compContactInfo = [
        config?.companyAddress ? `End: ${config.companyAddress}` : "",
        config?.companyPhone ? `Tel: ${config.companyPhone}` : "",
        config?.companyEmail ? `E-mail: ${config.companyEmail}` : ""
      ].filter(Boolean).join(" | ");
    }
    drawField(doc, "Contato do Empregador", compContactInfo, 18, y);
    drawField(doc, "Tolerância de Ponto", `${config?.tolerancia_minutos ?? 10} minutos diários`, 150, y);

    y += 14;
    drawField(doc, "Dia de Pagamento", `${config?.dia_pagamento ?? 5}º dia útil`, 18, y);
    drawField(doc, "Dia de Adiantamento (Vale)", `${config?.dia_vale ?? 20} de cada mês`, 70, y);
    drawField(doc, "Saldo Banco Inicial", `${config?.banco_horas_inicial ?? 0} minutos`, 110, y);

    y += 20;

    // 4. JORNADA DE TRABALHO
    y = drawSectionHeader(doc, "4. Horários de Jornada Semanal", y);

    const formatJornada = (dayKey) => {
      const day = config?.[dayKey];
      if (!day || !day.horas_esperadas) return "Folga";
      return `${day.entrada1 || "00:00"} - ${day.saida1 || "00:00"} | ${day.entrada2 || "00:00"} - ${day.saida2 || "00:00"}`;
    };

    drawField(doc, "Segunda-feira", formatJornada("jornada_seg"), 18, y, 8);
    drawField(doc, "Terça-feira", formatJornada("jornada_ter"), 75, y, 8);
    drawField(doc, "Quarta-feira", formatJornada("jornada_qua"), 135, y, 8);

    y += 12;
    drawField(doc, "Quinta-feira", formatJornada("jornada_qui"), 18, y, 8);
    drawField(doc, "Sexta-feira", formatJornada("jornada_sex"), 75, y, 8);
    drawField(doc, "Sábado / Plantões", "Conforme escala definida pelo gestor", 135, y, 8);

    y += 28;

    // Signatures
    if (signatureImg) {
      try {
        doc.addImage(signatureImg, "PNG", 42, y - 11, 26, 10);
      } catch (e) {
        console.error("Error drawing signature in Ficha de Registro:", e);
      }
    }

    doc.setDrawColor(100, 116, 139);
    doc.line(25, y, 85, y);
    doc.line(125, y, 185, y);

    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 41, 59);
    doc.text("Assinatura do Empregador", 37, y + 5);
    doc.text("Assinatura do Empregado", 137, y + 5);

    // Draw footer at bottom
    drawCompanyFooter(doc, config);

    const filename = `ficha_registro_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // 2. QUADRO DE HORARIO
  async generateQuadroHorario(colab, config, returnBlob = false, preloadedLogo = null, preloadedSig = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);
    const signatureImg = preloadedSig || (config?.companyAssinaturaUrl ? await loadImage(config.companyAssinaturaUrl) : null);

    drawCompanyTitleHeader(doc, config, "QUADRO DE HORÁRIO DE TRABALHO INDIVIDUAL", logoImg);

    let posY = 38;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    
    doc.text("Colaborador:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 45, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("CPF:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.cpf || "-", 45, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("Contratante:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.contratante || "Sempre Joias", 45, posY);

    posY += 15;
    doc.setFont("helvetica", "normal");
    doc.text("Em cumprimento às exigências da legislação do trabalho vigente, estabelece-se o seguinte quadro individualizado de horário de trabalho e descanso semanal:", 18, posY, { maxWidth: 172 });

    posY += 15;

    // Draw Shift Table
    doc.setFillColor(241, 245, 249);
    doc.rect(18, posY, 172, 8, "F");
    doc.setDrawColor(148, 163, 184);
    doc.rect(18, posY, 172, 8, "S");

    doc.setFont("helvetica", "bold");
    doc.text("Dia da Semana", 22, posY + 5.5);
    doc.text("Entrada 1", 60, posY + 5.5);
    doc.text("Saída 1", 90, posY + 5.5);
    doc.text("Entrada 2", 120, posY + 5.5);
    doc.text("Saída 2", 150, posY + 5.5);
    doc.text("Total", 175, posY + 5.5);

    const days = [
      { key: "jornada_seg", label: "Segunda-feira" },
      { key: "jornada_ter", label: "Terça-feira" },
      { key: "jornada_qua", label: "Quarta-feira" },
      { key: "jornada_qui", label: "Quinta-feira" },
      { key: "jornada_sex", label: "Sexta-feira" }
    ];

    posY += 8;
    doc.setFont("helvetica", "normal");

    days.forEach(day => {
      const j = config?.[day.key];
      doc.rect(18, posY, 172, 8, "S");
      doc.text(day.label, 22, posY + 5.5);
      if (j && j.horas_esperadas) {
        doc.text(j.entrada1 || "00:00", 60, posY + 5.5);
        doc.text(j.saida1 || "00:00", 90, posY + 5.5);
        doc.text(j.entrada2 || "00:00", 120, posY + 5.5);
        doc.text(j.saida2 || "00:00", 150, posY + 5.5);
        const hrs = (j.horas_esperadas / 60).toFixed(1);
        doc.text(`${hrs} hrs`, 175, posY + 5.5);
      } else {
        doc.text("Folga / Dispensado", 60, posY + 5.5);
      }
      posY += 8;
    });

    // Saturday row
    doc.rect(18, posY, 172, 8, "S");
    doc.text("Sábado", 22, posY + 5.5);
    doc.text("Conforme Escala / Plantão de Sábado", 60, posY + 5.5);
    posY += 8;

    // Sunday row
    doc.rect(18, posY, 172, 8, "S");
    doc.text("Domingo", 22, posY + 5.5);
    doc.text("DSR (Descanso Semanal Remunerado)", 60, posY + 5.5);

    posY += 25;
    const textLegal = "O colaborador obriga-se a registrar diariamente suas batidas de ponto no relógio registrador eletrônico disponibilizado, devendo as eventuais variações de horário ser apuradas no banco de horas nos termos do acordo coletivo.";
    const splitText = doc.splitTextToSize(textLegal, 172);
    doc.text(splitText, 18, posY);

    posY += 35;
    doc.text(`${getCompanyCityState(config)}, ${new Date().toLocaleDateString("pt-BR")}.`, 18, posY);

    posY += 30;
    drawEmployerSignature(doc, posY, signatureImg);
    doc.line(18, posY, 90, posY);
    doc.line(120, posY, 192, posY);

    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.text(config?.contratante || "Sempre Joias", 18, posY + 4);
    doc.text("Empregador", 18, posY + 8);

    doc.text(config?.nome_completo || colab?.name || "-", 120, posY + 4);
    doc.text("Assinatura do Empregado", 120, posY + 8);

    const filename = `quadro_horario_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // 3. SOLICITAÇÃO DE VALE-TRANSPORTE (VT)
  async generateSolicitacaoVT(colab, config, data, returnBlob = false, preloadedLogo = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);

    drawCompanyTitleHeader(doc, config, "SOLICITAÇÃO DE VALE-TRANSPORTE (VT)", logoImg);

    let posY = 38;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    doc.text("Eu,", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 26, posY);
    doc.setFont("helvetica", "normal");
    doc.text(", inscrito(a) no CPF sob o nº", 115, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.cpf || "-", 158, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("residente e domiciliado à", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.endereco || "___________________________________________", 58, posY);

    posY += 10;
    doc.setFont("helvetica", "normal");
    const p1 = "Nos termos da Lei nº 7.418 de 16/12/85, alterada pela Lei nº 7.619 de 30/09/87 e Decreto nº 95.247/87, solicito que me seja fornecido o benefício do Vale-Transporte para o meu deslocamento diário residência-trabalho e vice-versa.";
    const splitP1 = doc.splitTextToSize(p1, 172);
    doc.text(splitP1, 18, posY);

    posY += 20;
    doc.setFont("helvetica", "bold");
    doc.text("DADOS DO DESLOCAMENTO DIÁRIO:", 18, posY);

    posY += 8;
    // Box
    doc.setFillColor(248, 250, 252);
    doc.rect(18, posY, 172, 32, "F");
    doc.setDrawColor(203, 213, 225);
    doc.rect(18, posY, 172, 32, "S");

    doc.setFontSize(9.5);
    doc.setFont("helvetica", "normal");
    doc.text("Empresa de Transporte:", 22, posY + 8);
    doc.setFont("helvetica", "bold");
    doc.text(data.empresaTransporte || "SPTrans (Bilhete Único)", 60, posY + 8);

    doc.setFont("helvetica", "normal");
    doc.text("Trajeto / Linhas:", 22, posY + 16);
    doc.setFont("helvetica", "bold");
    doc.text(data.trajetoLinhas || "Residência / Trabalho / Residência", 60, posY + 16);

    doc.setFont("helvetica", "normal");
    doc.text("Quantidade Diária:", 22, posY + 24);
    doc.setFont("helvetica", "bold");
    doc.text(`${data.quantidadeDiaria || "2"} passagens`, 60, posY + 24);
    doc.setFont("helvetica", "normal");
    doc.text("Tarifa Unitária:", 110, posY + 24);
    doc.setFont("helvetica", "bold");
    doc.text(data.tarifaUnitaria ? formatCurrency(data.tarifaUnitaria) : "R$ 5,00", 135, posY + 24);

    posY += 42;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const pLegal = "Declaro estar ciente de que o desconto de até 6% (seis por cento) do meu salário básico será efetuado em folha de pagamento, conforme legislação vigente. Comprometo-me, também, a informar imediatamente qualquer alteração de meu endereço residencial.";
    const splitLegal = doc.splitTextToSize(pLegal, 172);
    doc.text(splitLegal, 18, posY);

    posY += 25;
    doc.text(`${getCompanyCityState(config)}, ${new Date().toLocaleDateString("pt-BR")}.`, 18, posY);

    posY += 30;
    doc.line(45, posY, 165, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 105, posY + 5, { align: "center" });
    doc.text("Assinatura do Beneficiário", 105, posY + 9, { align: "center" });

    const filename = `solicitacao_vt_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // 4. OPÇÃO E DESISTÊNCIA DE VALE TRANSPORTE
  async generateDesistenciaVT(colab, config, data, returnBlob = false, preloadedLogo = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);

    drawCompanyTitleHeader(doc, config, "TERMO DE OPÇÃO E DECLARAÇÃO DE DESISTÊNCIA DE VT", logoImg);

    let posY = 38;
    doc.setFontSize(10.5);
    doc.setFont("helvetica", "normal");
    
    // Main Body Statement
    const introText = `Eu, ${config?.nome_completo || colab?.name || "_________________________________"}, portador(a) do CPF nº ${config?.cpf || "__________________"}, contratado(a) por ${config?.contratante || "Sempre Joias"} no cargo de colaborador(a), residente no endereço ${config?.endereco || "___________________________________________"}, em conformidade com o que dispõe o Decreto nº 95.247/87, art. 7º e a Lei nº 7.418/85, venho por meio deste termo declarar a minha opção em relação ao Vale Transporte:`;

    const splitIntro = doc.splitTextToSize(introText, 172);
    doc.text(splitIntro, 18, posY);
    posY += (splitIntro.length * 6.5) + 12;

    // Desistência Checkbox Text Box
    doc.setFillColor(248, 250, 252);
    doc.rect(18, posY, 172, 38, "F");
    doc.setDrawColor(203, 213, 225);
    doc.rect(18, posY, 172, 38, "S");

    // Checkbox 1 (Desistência)
    doc.rect(24, posY + 6, 4, 4, "S");
    // Draw "X" inside the box to show checkmark
    doc.setFont("helvetica", "bold");
    doc.text("X", 25, posY + 9.5);
    
    doc.setFont("helvetica", "bold");
    doc.text("DESISTÊNCIA / NÃO OPÇÃO PELO VALE TRANSPORTE", 32, posY + 9.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const reasonText = `Declaro que recuso e desisto de receber o benefício de Vale Transporte, em virtude de utilizar meio de locomoção próprio (${data.meioLocomocao || "veículo particular"}) ou por residir próximo ao local de trabalho, assumindo total responsabilidade pelo meu deslocamento diário residência-trabalho-residência.`;
    const splitReason = doc.splitTextToSize(reasonText, 155);
    doc.text(splitReason, 32, posY + 16);

    posY += 50;

    // Legal responsibilities clause
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const pLegal = "Declaro estar ciente de que a declaração falsa ou o uso indevido do Vale Transporte constituem falta grave, sujeitando-me às penalidades da lei, inclusive à demissão por justa causa, conforme o art. 482 da CLT, c/c o parágrafo único do art. 7º do Decreto nº 95.247/87.";
    const splitLegal = doc.splitTextToSize(pLegal, 172);
    doc.text(splitLegal, 18, posY);
    posY += (splitLegal.length * 6) + 12;

    const pUpdate = "Comprometo-me, ainda, a comunicar imediatamente ao departamento de recursos humanos qualquer alteração no meu endereço residencial ou meios de transporte utilizados para locomoção diária.";
    const splitUpdate = doc.splitTextToSize(pUpdate, 172);
    doc.text(splitUpdate, 18, posY);
    posY += (splitUpdate.length * 6) + 20;

    // Date
    doc.text(`${getCompanyCityState(config)}, ${new Date().toLocaleDateString("pt-BR")}.`, 18, posY);

    posY += 35;

    // Signature
    doc.setDrawColor(148, 163, 184);
    doc.line(45, posY, 165, posY);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 105, posY + 5, { align: "center" });
    doc.text("Assinatura do Colaborador", 105, posY + 9, { align: "center" });

    const filename = `desistencia_vt_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // 5. AVISO PREVIO (TRABALHADO)
  async generateAvisoPrevioTrabalhado(colab, config, data, returnBlob = false, preloadedLogo = null, preloadedSig = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);
    const signatureImg = preloadedSig || (config?.companyAssinaturaUrl ? await loadImage(config.companyAssinaturaUrl) : null);

    drawCompanyTitleHeader(doc, config, "AVISO PRÉVIO TRABALHADO PELO EMPREGADOR", logoImg);

    let posY = 38;
    doc.setFontSize(10.5);
    doc.setFont("helvetica", "normal");

    doc.text("Ao Sr(a):", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 38, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("CPF nº:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.cpf || "-", 38, posY);

    posY += 15;
    doc.setFont("helvetica", "normal");
    
    const p1 = `Comunicamos-lhe que, por razões de ordem administrativa, não serão mais utilizados os seus serviços pela nossa empresa. Assim sendo, notificamos que seu contrato de trabalho será rescindido a partir de ${formatDate(data.dataFimAviso)}.`;
    const splitP1 = doc.splitTextToSize(p1, 172);
    doc.text(splitP1, 18, posY);
    posY += (splitP1.length * 6) + 6;

    const diasCalculo = data.diasAvisoProporcional || 30;
    const p2 = `Conforme preceitua a Lei nº 12.506/2011, V. Sa. cumprirá o aviso prévio trabalhado de ${diasCalculo} dias, com início no dia ${formatDate(data.dataInicioAviso)} e encerramento em ${formatDate(data.dataFimAviso)}.`;
    const splitP2 = doc.splitTextToSize(p2, 172);
    doc.text(splitP2, 18, posY);
    posY += (splitP2.length * 6) + 6;

    const optReducao = data.opcaoReducao === "dias" 
      ? "pela redução de 7 (sete) dias corridos ao final do período, estando dispensado(a) de comparecer ao trabalho nesses dias."
      : "pela redução de 2 (duas) horas diárias na jornada de trabalho regular, sem prejuízo salarial.";
    const p3 = `Em conformidade com o artigo 488 da CLT, V. Sa. optou ${optReducao}`;
    const splitP3 = doc.splitTextToSize(p3, 172);
    doc.text(splitP3, 18, posY);
    posY += (splitP3.length * 6) + 12;

    const p4 = "Pedimos que apresente sua Carteira de Trabalho e Previdência Social para a respectiva baixa das anotações legais.";
    const splitP4 = doc.splitTextToSize(p4, 172);
    doc.text(splitP4, 18, posY);

    posY = Math.max(posY + 25, 200);
    doc.text(`${getCompanyCityState(config)}, ${new Date().toLocaleDateString("pt-BR")}.`, 18, posY);

    posY += 25;
    drawEmployerSignature(doc, posY, signatureImg);
    doc.line(18, posY, 90, posY);
    doc.line(120, posY, 192, posY);

    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.text(config?.contratante || "Sempre Joias", 18, posY + 4);
    doc.text("Empregador", 18, posY + 8);

    doc.text(config?.nome_completo || colab?.name || "-", 120, posY + 4);
    doc.text("Ciente em: ____/____/________", 120, posY + 8);

    const filename = `aviso_previo_trabalhado_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // 6. AVISO PREVIO INDENIZADO
  async generateAvisoPrevioIndenizado(colab, config, data, returnBlob = false, preloadedLogo = null, preloadedSig = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);
    const signatureImg = preloadedSig || (config?.companyAssinaturaUrl ? await loadImage(config.companyAssinaturaUrl) : null);

    drawCompanyTitleHeader(doc, config, "AVISO PRÉVIO INDENIZADO PELO EMPREGADOR", logoImg);

    let posY = 38;
    doc.setFontSize(10.5);
    doc.setFont("helvetica", "normal");

    doc.text("Ao Sr(a):", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 38, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("CPF nº:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.cpf || "-", 38, posY);

    posY += 15;
    doc.setFont("helvetica", "normal");

    const p1 = `Comunicamos-lhe que a partir do dia ${formatDate(data.dataDemissao)} a empresa rescindirá o seu contrato de trabalho, desligando-o de suas funções profissionais de forma imediata.`;
    const splitP1 = doc.splitTextToSize(p1, 172);
    doc.text(splitP1, 18, posY);
    posY += (splitP1.length * 6) + 6;

    const diasCalculo = data.diasAvisoProporcional || 30;
    const p2 = `Em virtude deste desligamento imediato, a empresa opta por indenizar o período correspondente ao Aviso Prévio Proporcional de ${diasCalculo} dias.`;
    const splitP2 = doc.splitTextToSize(p2, 172);
    doc.text(splitP2, 18, posY);
    posY += (splitP2.length * 6) + 6;

    const p3 = "As verbas rescisórias devidas a título de saldo de salário, férias proporcionais, terço constitucional, décimo terceiro salário proporcional e indenização do aviso prévio serão pagas nos prazos definidos por lei.";
    const splitP3 = doc.splitTextToSize(p3, 172);
    doc.text(splitP3, 18, posY);
    posY += (splitP3.length * 6) + 12;

    const p4 = "Agradecemos pelo período trabalhado e solicitamos seu comparecimento portando a Carteira de Trabalho para baixa.";
    const splitP4 = doc.splitTextToSize(p4, 172);
    doc.text(splitP4, 18, posY);

    posY = Math.max(posY + 25, 200);
    doc.text(`${getCompanyCityState(config)}, ${new Date().toLocaleDateString("pt-BR")}.`, 18, posY);

    posY += 25;
    drawEmployerSignature(doc, posY, signatureImg);
    doc.line(18, posY, 90, posY);
    doc.line(120, posY, 192, posY);

    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.text(config?.contratante || "Sempre Joias", 18, posY + 4);
    doc.text("Empregador", 18, posY + 8);

    doc.text(config?.nome_completo || colab?.name || "-", 120, posY + 4);
    doc.text("Ciente em: ____/____/________", 120, posY + 8);

    const filename = `aviso_previo_indenizado_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // 7. COMUNICADO JUSTA CAUSA
  async generateComunicadoJustaCausa(colab, config, data, returnBlob = false, preloadedLogo = null, preloadedSig = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);
    const signatureImg = preloadedSig || (config?.companyAssinaturaUrl ? await loadImage(config.companyAssinaturaUrl) : null);

    drawCompanyTitleHeader(doc, config, "COMUNICADO DE DISPENSA POR JUSTA CAUSA", logoImg);

    let posY = 38;
    doc.setFontSize(10.5);
    doc.setFont("helvetica", "normal");

    doc.text("Ao Sr(a):", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 38, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("CPF nº:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.cpf || "-", 38, posY);

    posY += 15;
    doc.setFont("helvetica", "normal");

    const p1 = `Notificamos V. Sa., por meio deste comunicado, que a partir da presente data seu contrato de trabalho está rescindido por JUSTA CAUSA, desvinculando-o(a) definitivamente dos quadros da empresa.`;
    const splitP1 = doc.splitTextToSize(p1, 172);
    doc.text(splitP1, 18, posY);
    posY += (splitP1.length * 6) + 6;

    const p2 = `Esta drástica medida disciplinar foi adotada em decorrência do grave descumprimento contratual e legal caracterizado pelos seguintes atos:\n\n"${data.motivoJustaCausa || "Ausências reiteradas sem justificativa médica"}"`;
    const splitP2 = doc.splitTextToSize(p2, 172);
    doc.text(splitP2, 18, posY);
    posY += (splitP2.length * 6) + 6;

    const cltItem = data.artigoCLT || "artigo 482, alínea 'e' (desídia)";
    const p3 = `Tais atos configuram a hipótese tipificada no ${cltItem} da Consolidação das Leis do Trabalho (CLT), tornando insustentável a manutenção do vínculo de confiança essencial à relação laboral.`;
    const splitP3 = doc.splitTextToSize(p3, 172);
    doc.text(splitP3, 18, posY);
    posY += (splitP3.length * 6) + 12;

    const p4 = "Compareça à empresa portando a CTPS para os registros formais de baixa e acerto das poucas verbas remanescentes devidas.";
    const splitP4 = doc.splitTextToSize(p4, 172);
    doc.text(splitP4, 18, posY);

    posY = Math.max(posY + 25, 200);
    doc.text(`${getCompanyCityState(config)}, ${new Date().toLocaleDateString("pt-BR")}.`, 18, posY);

    posY += 25;
    drawEmployerSignature(doc, posY, signatureImg);
    doc.line(18, posY, 90, posY);
    doc.line(120, posY, 192, posY);

    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.text(config?.contratante || "Sempre Joias", 18, posY + 4);
    doc.text("Empregador", 18, posY + 8);

    doc.text(config?.nome_completo || colab?.name || "-", 120, posY + 4);
    doc.text("Assinatura do Colaborador", 120, posY + 8);

    // Witness signatures
    posY += 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text("Testemunhas (caso o colaborador recuse-se a receber este termo):", 18, posY);

    posY += 12;
    doc.line(18, posY, 90, posY);
    doc.line(120, posY, 192, posY);
    doc.text("Nome: _____________________________", 18, posY + 4);
    doc.text("Nome: _____________________________", 120, posY + 4);

    const filename = `comunicado_justa_causa_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // 8. TERMO DE ADVERTÊNCIA OU SUSPENSÃO DISCIPLINAR
  async generateOcorrencia(colab, config, type, data, returnBlob = false, preloadedLogo = null, preloadedSig = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);
    const signatureImg = preloadedSig || (config?.companyAssinaturaUrl ? await loadImage(config.companyAssinaturaUrl) : null);

    const isSusp = type === "suspensao";
    const title = isSusp ? "COMUNICADO DE SUSPENSÃO DISCIPLINAR" : "COMUNICADO DE ADVERTÊNCIA DISCIPLINAR";

    drawCompanyTitleHeader(doc, config, title, logoImg);

    let posY = 38;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    
    doc.text("Ao Sr(a):", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 38, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("CPF nº:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.cpf || "-", 38, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("Contratante:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.contratante || "Sempre Joias", 38, posY);

    posY += 15;
    doc.setFont("helvetica", "normal");
    
    // Message Body
    const paragraphs = [];
    if (isSusp) {
      paragraphs.push(
        `Por meio deste comunicado, notificamos V. Sa. de que está suspenso(a) de suas atividades profissionais pelo período de ${data.diasSuspensao || 1} dia(s), com início em ${formatDate(data.dataInicioSuspensao)} e data de retorno prevista para o dia ${formatDate(data.dataRetornoSuspensao)}.`
      );
      paragraphs.push(
        `Esta medida disciplinar está sendo aplicada em decorrência do seguinte fato ocorrido no dia ${formatDate(data.dataOcorrencia)}:\n\n"${data.motivoOcorrencia}"`
      );
    } else {
      paragraphs.push(
        `Por meio deste comunicado, aplicamos a V. Sa. a penalidade de ADVERTÊNCIA DISCIPLINAR, em decorrência do seguinte fato ocorrido no dia ${formatDate(data.dataOcorrencia)}:\n\n"${data.motivoOcorrencia}"`
      );
    }

    paragraphs.push(
      "Salientamos que a reincidência em comportamentos que firam as normas internas da empresa ou a legislação do trabalho vigente poderá acarretar sanções disciplinares mais severas, inclusive a rescisão do contrato de trabalho por justa causa, nos termos do Art. 482 da CLT."
    );

    paragraphs.push(
      "Solicitamos sua colaboração para que fatos como este não voltem a ocorrer, restabelecendo o bom andamento de nossas relações profissionais."
    );

    doc.setFontSize(10.5);
    doc.setTextColor(15, 23, 42);

    paragraphs.forEach(p => {
      const splitText = doc.splitTextToSize(p, 172);
      doc.text(splitText, 18, posY);
      posY += (splitText.length * 6) + 6;
    });

    posY = Math.max(posY, 190);
    
    // Date & Location
    doc.setFont("helvetica", "normal");
    doc.text(`${getCompanyCityState(config)}, ${new Date().toLocaleDateString("pt-BR")}.`, 18, posY);

    posY += 25;

    // Signatures
    doc.setDrawColor(148, 163, 184);
    drawEmployerSignature(doc, posY, signatureImg);
    doc.line(18, posY, 90, posY);
    doc.line(120, posY, 192, posY);

    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.text(config?.contratante || "Sempre Joias", 18, posY + 4);
    doc.text("Representante Legal", 18, posY + 8);

    doc.text(config?.nome_completo || colab?.name || "-", 120, posY + 4);
    doc.text("Assinatura do Colaborador", 120, posY + 8);

    // Witness signatures at the bottom
    posY += 25;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Testemunhas (caso o colaborador recuse-se a assinar):", 18, posY);

    posY += 15;
    doc.line(18, posY, 90, posY);
    doc.line(120, posY, 192, posY);

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Nome: _____________________________", 18, posY + 4);
    doc.text("CPF: ______________________________", 18, posY + 8);

    doc.text("Nome: _____________________________", 120, posY + 4);
    doc.text("CPF: ______________________________", 120, posY + 8);

    const filename = `${type}_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // 9. AVISO DE FÉRIAS
  async generateFerias(colab, config, data, returnBlob = false, preloadedLogo = null, preloadedSig = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);
    const signatureImg = preloadedSig || (config?.companyAssinaturaUrl ? await loadImage(config.companyAssinaturaUrl) : null);

    drawCompanyTitleHeader(doc, config, "AVISO DE CONCESSÃO DE FÉRIAS", logoImg);

    let posY = 38;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    doc.text("Ao Colaborador(a):", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 55, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("CPF nº:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.cpf || "-", 55, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("Data de Admissão:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.data_admissao ? formatDate(config.data_admissao) : "-", 55, posY);

    posY += 6;
    doc.setFont("helvetica", "normal");
    doc.text("Empresa Contratante:", 18, posY);
    doc.setFont("helvetica", "bold");
    doc.text(config?.contratante || "Sempre Joias", 55, posY);

    posY += 18;
    doc.setFont("helvetica", "normal");
    
    // Main Body
    const p1 = `Comunicamos-lhe que, em conformidade com as disposições legais vigentes, ser-lhe-ão concedidas férias regulamentares relativas ao período aquisitivo de ${data.periodoAquisitivo || "do ano anterior"}, a serem gozadas no período de:`;
    
    const splitP1 = doc.splitTextToSize(p1, 172);
    doc.text(splitP1, 18, posY);
    posY += (splitP1.length * 6) + 8;

    // Date Block Box
    doc.setFillColor(248, 250, 252);
    doc.rect(18, posY, 172, 28, "F");
    doc.setDrawColor(203, 213, 225);
    doc.rect(18, posY, 172, 28, "S");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(`DATA DE INÍCIO:  ${formatDate(data.dataInicio)}`, 25, posY + 8);
    doc.text(`DATA DE TÉRMINO: ${formatDate(data.dataFim)}`, 25, posY + 16);
    doc.text(`RETORNO AO TRABALHO: ${formatDate(data.dataRetorno)}`, 25, posY + 24);

    posY += 38;

    doc.setFont("helvetica", "normal");
    const p2 = "Em virtude desta concessão, solicitamos que nos apresente a sua Carteira de Trabalho e Previdência Social (CTPS) e/ou registre no sistema para as devidas anotações legais, as quais deverão ser efetuadas antes do início das referidas férias.";
    const splitP2 = doc.splitTextToSize(p2, 172);
    doc.text(splitP2, 18, posY);
    posY += (splitP2.length * 6) + 12;

    const p3 = "Declaro que fui notificado em tempo hábil da concessão de minhas férias nas condições acima descritas.";
    doc.setFont("helvetica", "bold");
    const splitP3 = doc.splitTextToSize(p3, 172);
    doc.text(splitP3, 18, posY);
    posY += (splitP3.length * 6) + 20;

    // Date
    doc.setFont("helvetica", "normal");
    doc.text(`${getCompanyCityState(config)}, ${new Date().toLocaleDateString("pt-BR")}.`, 18, posY);

    posY += 30;

    // Signatures
    doc.setDrawColor(148, 163, 184);
    drawEmployerSignature(doc, posY, signatureImg);
    doc.line(18, posY, 90, posY);
    doc.line(120, posY, 192, posY);

    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.text(config?.contratante || "Sempre Joias", 18, posY + 4);
    doc.text("Representante Legal", 18, posY + 8);

    doc.text(config?.nome_completo || colab?.name || "-", 120, posY + 4);
    doc.text("Assinatura do Colaborador", 120, posY + 8);

    const filename = `ferias_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // 11. RESUMO DE ADIANTAMENTO SALARIAL (VALE)
  async generateOleriteVale(colab, config, data, returnBlob = false, preloadedLogo = null, preloadedSignature = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);
    const signatureImg = preloadedSignature || (config?.companyAssinaturaUrl ? await loadImage(config.companyAssinaturaUrl) : null);

    drawCompanyTitleHeader(doc, config, "DEMONSTRATIVO DE ADIANTAMENTO SALARIAL", logoImg);

    let posY = 38;
    
    // Employee Header Card
    doc.setFillColor(248, 250, 252);
    doc.rect(15, posY, 180, 28, "F");
    doc.setDrawColor(203, 213, 225);
    doc.rect(15, posY, 180, 28, "S");

    drawField(doc, "Colaborador", config?.nome_completo || colab?.name || "-", 18, posY + 6, 9.5);
    drawField(doc, "CPF", config?.cpf || "-", 120, posY + 6);
    drawField(doc, "Cargo / Função", config?.funcao || "-", 18, posY + 18);
    drawField(doc, "Mês de Referência", data.mesAno || "-", 120, posY + 18);

    posY += 36;

    // Detailed Table or Fields for Vale
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(30, 41, 59);
    doc.text("DETALHAMENTO DO ADIANTAMENTO:", 15, posY);

    posY += 6;
    doc.setFillColor(248, 250, 252);
    doc.rect(15, posY, 180, 36, "F");
    doc.setDrawColor(203, 213, 225);
    doc.rect(15, posY, 180, 36, "S");

    doc.setFontSize(9.5);
    doc.setFont("helvetica", "normal");
    doc.text("Salário Base do Cargo:", 20, posY + 8);
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(data.salarioBase || 0), 90, posY + 8);

    doc.setFont("helvetica", "normal");
    doc.text("Valor do Adiantamento (Vale):", 20, posY + 16);
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(data.valeAdiantamento || 0), 90, posY + 16);

    doc.setFont("helvetica", "normal");
    doc.text("Descontos:", 20, posY + 24);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(220, 38, 38); // Red
    doc.text(`- ${formatCurrency(data.descontos || 0)}`, 90, posY + 24);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 41, 59);
    doc.text("VALOR LÍQUIDO A RECEBER:", 20, posY + 31);
    doc.setFont("helvetica", "extrabold");
    doc.setTextColor(22, 163, 74); // Green
    doc.text(formatCurrency(data.valorLiquido || 0), 90, posY + 31);

    posY += 46;

    if (data.observacoes) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(30, 41, 59);
      doc.text("OBSERVAÇÕES / NOTAS:", 15, posY);
      posY += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(71, 85, 105);
      const splitObs = doc.splitTextToSize(data.observacoes, 175);
      doc.text(splitObs, 15, posY);
      posY += splitObs.length * 4.5 + 5;
    } else {
      posY += 10;
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(30, 41, 59);
    doc.text("Declaro ter recebido a título de adiantamento de salário a importância líquida descrita acima.", 15, posY);

    posY += 18;
    doc.text(`${getCompanyCityState(config)}, ${new Date().toLocaleDateString("pt-BR")}.`, 15, posY);

    posY += 26;
    // Signatures
    doc.line(18, posY, 90, posY);
    doc.line(120, posY, 192, posY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text(config?.companyName || config?.contratante || "Sempre Joias", 18, posY + 4);
    doc.setFont("helvetica", "normal");
    doc.text("Representante Legal", 18, posY + 8);

    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 120, posY + 4);
    doc.setFont("helvetica", "normal");
    doc.text("Assinatura do Colaborador", 120, posY + 8);

    drawEmployerSignature(doc, posY, signatureImg);

    const filename = `resumo_vale_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // 12. RESUMO DE PAGAMENTO MENSAL (SALÁRIO)
  async generateOleritePagamento(colab, config, data, returnBlob = false, preloadedLogo = null, preloadedSignature = null) {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const logoImg = preloadedLogo || (config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null);
    const signatureImg = preloadedSignature || (config?.companyAssinaturaUrl ? await loadImage(config.companyAssinaturaUrl) : null);

    drawCompanyTitleHeader(doc, config, "DEMONSTRATIVO DE PAGAMENTO MENSAL", logoImg);

    let posY = 38;
    
    // Employee Header Card
    doc.setFillColor(248, 250, 252);
    doc.rect(15, posY, 180, 28, "F");
    doc.setDrawColor(203, 213, 225);
    doc.rect(15, posY, 180, 28, "S");

    drawField(doc, "Colaborador", config?.nome_completo || colab?.name || "-", 18, posY + 6, 9.5);
    drawField(doc, "CPF", config?.cpf || "-", 120, posY + 6);
    drawField(doc, "Cargo / Função", config?.funcao || "-", 18, posY + 18);
    drawField(doc, "Mês de Referência", data.mesAno || "-", 120, posY + 18);

    posY += 36;

    // Detailed Table or Fields for Pagamento
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(30, 41, 59);
    doc.text("DETALHAMENTO DO PAGAMENTO:", 15, posY);

    posY += 6;
    doc.setFillColor(248, 250, 252);
    doc.rect(15, posY, 180, 64, "F");
    doc.setDrawColor(203, 213, 225);
    doc.rect(15, posY, 180, 64, "S");

    doc.setFontSize(9.5);
    doc.setFont("helvetica", "normal");
    doc.text("Salário Base:", 20, posY + 8);
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(data.salarioBase || 0), 100, posY + 8);

    doc.setFont("helvetica", "normal");
    doc.text("Horas Trabalhadas no Mês:", 20, posY + 16);
    doc.setFont("helvetica", "bold");
    doc.text(String(data.horasTrabalhadas || "220"), 100, posY + 16);

    doc.setFont("helvetica", "normal");
    doc.text(`Horas Extras Realizadas (Qtde: ${data.horasExtras || 0}):`, 20, posY + 24);
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(data.valorHorasExtras || 0), 100, posY + 24);

    doc.setFont("helvetica", "normal");
    doc.text("Desconto de Adiantamento (Vale):", 20, posY + 34);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(220, 38, 38); // Red
    doc.text(`- ${formatCurrency(data.valeAdiantamento || 0)}`, 100, posY + 34);

    doc.setFont("helvetica", "normal");
    doc.text("Outros Descontos:", 20, posY + 42);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(220, 38, 38); // Red
    doc.text(`- ${formatCurrency(data.descontos || 0)}`, 100, posY + 42);

    doc.setDrawColor(203, 213, 225);
    doc.line(18, posY + 48, 192, posY + 48);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 41, 59);
    doc.text("VALOR LÍQUIDO A RECEBER:", 20, posY + 56);
    doc.setFont("helvetica", "extrabold");
    doc.setTextColor(22, 163, 74); // Green
    doc.text(formatCurrency(data.valorLiquido || 0), 100, posY + 56);

    posY += 74;

    if (data.observacoes) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(30, 41, 59);
      doc.text("OBSERVAÇÕES / NOTAS:", 15, posY);
      posY += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(71, 85, 105);
      const splitObs = doc.splitTextToSize(data.observacoes, 175);
      doc.text(splitObs, 15, posY);
      posY += splitObs.length * 4.5 + 5;
    } else {
      posY += 10;
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(30, 41, 59);
    doc.text("Declaro ter recebido a importância líquida descrita acima, referente ao pagamento do meu salário.", 15, posY);

    posY += 18;
    doc.text(`${getCompanyCityState(config)}, ${new Date().toLocaleDateString("pt-BR")}.`, 15, posY);

    posY += 26;
    // Signatures
    doc.line(18, posY, 90, posY);
    doc.line(120, posY, 192, posY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text(config?.companyName || config?.contratante || "Sempre Joias", 18, posY + 4);
    doc.setFont("helvetica", "normal");
    doc.text("Representante Legal", 18, posY + 8);

    doc.setFont("helvetica", "bold");
    doc.text(config?.nome_completo || colab?.name || "-", 120, posY + 4);
    doc.setFont("helvetica", "normal");
    doc.text("Assinatura do Colaborador", 120, posY + 8);

    drawEmployerSignature(doc, posY, signatureImg);

    const filename = `resumo_pagamento_${(config?.nome_completo || colab?.name || "sasa").replace(/\s+/g, "_").toLowerCase()}.pdf`;
    return outputPDF(doc, filename, returnBlob);
  },

  // Generates the PDF document based on parameters and inputs
  async generate(docType, colab, config, data, returnBlob = false) {
    const logoImg = config?.companyLogoUrl ? await loadImage(config.companyLogoUrl) : null;
    const signatureImg = config?.companyAssinaturaUrl ? await loadImage(config.companyAssinaturaUrl) : null;

    switch (docType) {
      case "ficha_registro":
        return await this.generateFichaRegistro(colab, config, returnBlob, logoImg);
      case "quadro_horario":
        return await this.generateQuadroHorario(colab, config, returnBlob, logoImg, signatureImg);
      case "solicitacao_vt":
        return await this.generateSolicitacaoVT(colab, config, data, returnBlob, logoImg);
      case "desistencia_vt":
        return await this.generateDesistenciaVT(colab, config, data, returnBlob, logoImg);
      case "aviso_previo_trabalhado":
      case "aviso_previo":
        return await this.generateAvisoPrevioTrabalhado(colab, config, data, returnBlob, logoImg, signatureImg);
      case "aviso_previo_indenizado":
        return await this.generateAvisoPrevioIndenizado(colab, config, data, returnBlob, logoImg, signatureImg);
      case "comunicado_justa_causa":
        return await this.generateComunicadoJustaCausa(colab, config, data, returnBlob, logoImg, signatureImg);
      case "advertencia":
        return await this.generateOcorrencia(colab, config, "advertencia", data, returnBlob, logoImg, signatureImg);
      case "suspensao":
        return await this.generateOcorrencia(colab, config, "suspensao", data, returnBlob, logoImg, signatureImg);
      case "ferias":
        return await this.generateFerias(colab, config, data, returnBlob, logoImg, signatureImg);
      case "olerite_vale":
        return await this.generateOleriteVale(colab, config, data, returnBlob, logoImg, signatureImg);
      case "olerite_pagamento":
        return await this.generateOleritePagamento(colab, config, data, returnBlob, logoImg, signatureImg);
      default:
        throw new Error(`Tipo de documento desconhecido: ${docType}`);
    }
  }
};
